// src/pages/HomePage.jsx (simplified)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Trophy, Star, Bell } from 'lucide-react';
import MatchCard from '../components/MatchCard';
import LeagueFilter from '../components/LeagueFilter';
import DateNavigator from '../components/DateNavigator';
import Header from '../components/Header';
import StatsCard from '../components/StatsCard';
import { matchesData } from '../data/matches';
import { shouldShowMatch } from '../utils/timeUtils';

function HomePage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Trigger refresh every 5 seconds untuk update visibility
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Loading effect
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1500);
  }, []);

  // Filter matches berdasarkan tanggal dan league
  const filteredMatches = useMemo(() => {
    return matchesData.filter(match => {
      const matchDate = new Date(match.dateISO);
      const isSameDate = matchDate.toDateString() === selectedDate.toDateString();
      const isSameLeague = selectedLeague === 'all' || match.league === selectedLeague;
      
      return isSameDate && isSameLeague;
    });
  }, [selectedDate, selectedLeague]);

  // Stats dengan useMemo
  const stats = useMemo(() => ({
    totalMatches: filteredMatches.length,
    topLeague: 'Premier League',
    liveMatches: filteredMatches.filter(m => m.status === 'live').length
  }), [filteredMatches]);

  // Handler dengan useCallback
  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleLeagueChange = useCallback((league) => {
    setSelectedLeague(league);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-football-green-900 to-gray-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-football-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float animation-delay-2000" />
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float animation-delay-4000" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        <Header />
        
        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <StatsCard
            icon={Trophy}
            title="Total Pertandingan"
            value={stats.totalMatches}
            color="from-yellow-400 to-yellow-600"
          />
          <StatsCard
            icon={Star}
            title="Liga Top"
            value={stats.topLeague}
            color="from-blue-400 to-blue-600"
          />
          <StatsCard
            icon={Bell}
            title="Sedang Berlangsung"
            value={stats.liveMatches}
            color="from-red-400 to-red-600"
          />
        </motion.div>

        {/* Date Navigator */}
        <DateNavigator
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
        />

        {/* League Filter */}
        <LeagueFilter
          selectedLeague={selectedLeague}
          onLeagueChange={handleLeagueChange}
        />

        {/* Matches Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 border-football-green-500 border-t-transparent rounded-full"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMatches.length > 0 ? (
              filteredMatches.map((match, index) => (
                <MatchCard 
                  key={match.id} 
                  match={match} 
                  index={index}
                />
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full flex flex-col items-center justify-center py-20 glass-card"
              >
                <Calendar className="w-20 h-20 text-white/50 mb-4" />
                <p className="text-xl text-white/70">Tidak ada pertandingan pada tanggal ini</p>
                <p className="text-white/50 mt-2">Silakan pilih tanggal lain</p>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HomePage;